import { defineStore } from "pinia";
import { computed, reactive, ref, watch } from "vue";
import { ChatRoom } from "./model/chat-room";
import { useChannelStore } from "../channel.store";
import { graphql as gql } from "@/generated";
import {
  mapPayloadToMessage,
  useChatStorage,
} from "./services/use-chat-storage";
import { useSocketsLazyEvents } from "@/composables/ably/use-sockets-lazy-events";
import type { IChatMessageWithLikes } from "./model/chat-model";
import { useQuery } from "@urql/vue";
import { useUserStore } from "../user.store";
import type {
  IChatMessageStatusUpdateWithPayload,
  IChatTranslationEvent,
} from "@f3/shared/chat-types";
import { getChatEventsChannelName } from "@f3/shared/chat-types";

type ChannelChatEvent =
  | (IChatMessageStatusUpdateWithPayload & { removed_by_id?: string })
  | IChatTranslationEvent;

const ChatRoomsForChannelDocument = gql(/* GraphQL */ `
  query ChatRoomsForChannel($channelId: String!) {
    chatRoomsForChannel(channel_id: $channelId) {
      id
      created_at
      updated_at
      title
      channel_id
      community_id
      deleted
      is_default
      chat_room_translations {
        title
        language
      }
      chat_messages {
        created_at
        ...ChatMessageForService
      }
    }
  }
`);

export const useChatStore = defineStore("chat", () => {
  const userStore = useUserStore();
  const channelStore = useChannelStore();
  const chatRooms = reactive(new Map<string, ChatRoom>());

  const chatStorage = useChatStorage();

  function getOrCreateChatRoom(
    id: string,
    title: string | null,
    titleTranslation: string | null,
    communityId: string | null,
    isDefault: boolean,
  ) {
    const existing = chatRooms.get(id);
    if (existing) return existing;

    const chatRoom = new ChatRoom({
      id,
      title,
      titleTranslation,
      communityId,
      isDefault,
    });

    return chatRoom;
  }

  function addRoom(
    id: string,
    title: string | null = null,
    titleTranslation: string | null = null,
    communityId: string | null = null,
    isDefault: boolean = false,
  ) {
    const chatRoom = getOrCreateChatRoom(
      id,
      title,
      titleTranslation,
      communityId,
      isDefault,
    );
    chatRooms.set(id, chatRoom);
    return chatRoom;
  }

  function removeRoom(id: string) {
    if (!chatRooms.has(id)) return;
    chatRooms.delete(id);
  }

  const {
    data: result,
    executeQuery: refetch,
    fetching,
  } = useQuery({
    query: ChatRoomsForChannelDocument,
    variables: computed(() => ({
      channelId: channelStore.channelSlug!,
      __language: userStore.userLanguage,
    })),
    pause: computed(() => !channelStore.channelSlug),
  });

  const chatRoomsInDb = computed(() => result.value?.chatRoomsForChannel ?? []);

  const isAllChannelRoomsVisible = computed(() => {
    return channelStore.channelCommunities.length < 2;
  });

  async function setLastMessageFromChatRooms(communityId: string | null) {
    let chatRoomId: string | null = null;
    let lastMessage: IChatMessageWithLikes | null = null;

    for (const room of chatRoomsInDb.value.values()) {
      if (communityId !== null && room.community_id !== communityId) {
        continue;
      }

      const message = room.chat_messages?.[0];
      if (!message) continue;

      if (
        !lastMessage ||
        message.created_at.localeCompare(lastMessage.message.timestamp) > 0
      ) {
        chatRoomId = room.id;
        lastMessage = chatStorage.formatChatMessage(message);
      }
    }

    if (!chatRoomId || !lastMessage) return;

    const chatRoom = chatRooms.get(chatRoomId);
    if (!chatRoom) return;

    lastChatMessage.value = lastMessage;
  }

  const selectedCommunity = computed<string | null>(() =>
    channelStore.channelCommunities.length === 1
      ? channelStore.channelCommunities[0]!.id
      : channelStore.selectedCommunity,
  );

  watch(
    () => selectedCommunity.value,
    async (value) => {
      await refetch();
      setLastMessageFromChatRooms(value);
    },
    { immediate: true },
  );

  watch(
    chatRoomsInDb,
    async () => {
      addChatRooms();
      await setLastMessageFromChatRooms(selectedCommunity.value);
    },
    { immediate: true },
  );

  function addChatRooms() {
    chatRooms.clear();
    if (!chatRoomsInDb.value) return;

    for (const room of chatRoomsInDb.value) {
      const chatRoom = getOrCreateChatRoom(
        room.id,
        room.title ?? null,
        room.chat_room_translations?.[0]?.title ?? null,
        room.community_id ?? null,
        room.is_default,
      );

      const lastChatMessageFragment = room.chat_messages?.at(0);
      if (lastChatMessageFragment) {
        const lastChatMessage = chatStorage.formatChatMessage(
          lastChatMessageFragment,
        );
        chatRoom.lastSeenMessageId = lastChatMessage.message.id;
        chatRoom.addMessages([lastChatMessage]);
      }

      chatRooms.set(room.id, chatRoom);
    }
  }

  const lastChatMessage = ref<IChatMessageWithLikes | null>(null);

  const ablyChatChannelName = computed(() =>
    getChatEventsChannelName({
      type: "channel",
      channelSlug: channelStore.channelSlug!,
    }),
  );

  function shouldIgnoreLastMessageEvent(chatRoomId: string) {
    const chatRoom = chatRooms.get(chatRoomId);

    // Ignore if not a channel chat room
    if (!chatRoom) return true;

    if (
      !isAllChannelRoomsVisible.value &&
      selectedCommunity.value !== null &&
      chatRoom.communityId !== selectedCommunity.value
    )
      return true;

    return false;
  }

  useSocketsLazyEvents<ChannelChatEvent>({
    channelName: ablyChatChannelName,
    listener: async (event) => {
      if (event.type === "chat-translation") return; // Handled by useProvideChat via watchChatMessages callback

      if (event.status !== "validated") return;

      let message: IChatMessageWithLikes | null = null;

      if (event.payload) {
        message = mapPayloadToMessage(
          event.payload,
          userStore.userId ?? null,
          userStore.userLanguage,
        );
      } else {
        message = await chatStorage.fetchMessage(event.chat_message_id);
      }

      if (!message || shouldIgnoreLastMessageEvent(message.message.chatRoomId))
        return;

      const chatRoom = chatRooms.get(message.message.chatRoomId);

      // If the message doesn't belong to any of the channel's rooms, ignore too
      if (!chatRoom) return;

      lastChatMessage.value = message;
      chatRoom.addMessages([message]);
    },
    options: { rewind: 1 },
  });

  return {
    lastChatMessage,
    chatRooms,
    isAllChannelRoomsVisible,
    addRoom,
    removeRoom,
    selectedCommunity,
    ablyChatChannelName,
    fetching,
  };
});
